import { mergeAttributes, Node } from '@tiptap/core';
import { ReactNodeViewRenderer } from '@tiptap/react';

import { type ClipType } from '@air/api/types';

import { isDurableMonikerWireUuid } from '~/components/v2/components/workspace/workspace-agent-panel/adapters/durable-plain-text/durableMonikerWireReferenceId';
import { PrivateWorkspaceAgentPillNodeView } from '~/components/v2/components/workspace/workspace-agent-panel/tiptap/PrivateWorkspaceAgentPillNodeView';
import { isWorkspaceAgentAssetClipType } from '~/components/v2/components/workspace/workspace-agent-panel/utils/isWorkspaceAgentAssetClipType';

/** TipTap node name for inline composer pills. */
export const WORKSPACE_AGENT_PILL_NODE_NAME = 'workspaceAgentPill';

/** Marker attribute the node-level `parseHTML` matches on. */
const PILL_MARKER_ATTRIBUTE = 'data-workspace-agent-pill';

/**
 * Own `data-*` per attr: TipTap's default parsing would `JSON.parse` a `2024` label into a number,
 * and a plain `id` attr would duplicate DOM ids.
 */
const PILL_DATA_ATTRIBUTES = {
  assetId: 'data-workspace-agent-pill-asset-id',
  assetType: 'data-workspace-agent-pill-asset-type',
  boardId: 'data-workspace-agent-pill-board-id',
  contextSourceFieldValueId: 'data-workspace-agent-pill-context-source-field-value-id',
  contextSourceId: 'data-workspace-agent-pill-context-source-id',
  durableWireEntityType: 'data-workspace-agent-pill-durable-wire-entity-type',
  ext: 'data-workspace-agent-pill-ext',
  id: 'data-workspace-agent-pill-id',
  label: 'data-workspace-agent-pill-label',
  thumbnailUrl: 'data-workspace-agent-pill-thumbnail-url',
  type: 'data-workspace-agent-pill-type',
  versionId: 'data-workspace-agent-pill-version-id',
} as const;

type WorkspaceAgentPillAttrName = keyof typeof PILL_DATA_ATTRIBUTES;

/**
 * Display data lives only here — no names on the wire; history re-resolves from the APIs. `id` is
 * the client uuid the `@[pill:<id>]` moniker in `content` points at.
 */
export type WorkspaceAgentPillDurableWireEntityType = 'asset' | 'assetVersion';

export type WorkspaceAgentPillNodeAttrs = {
  assetId: string | null;
  /** `null` for a pill minted before this attr existed, and for an unknown string the guard rejects. */
  assetType: ClipType | null;
  boardId: string | null;
  /**
   * Durable `instructions` wire form for asset pills. Hydration may set `versionId` for display while
   * the stored moniker remains `@[asset:…]` until the user explicitly inserted `@[assetVersion:…]`.
   */
  durableWireEntityType: WorkspaceAgentPillDurableWireEntityType | null;
  /** The brand kit value a `contextSourceFieldValue` pill points at, and the kit it belongs to. */
  contextSourceFieldValueId: string | null;
  contextSourceId: string | null;
  /** Display-only in the composer; not on wire pill metadata. */
  ext: string | null;
  id: string;
  label: string;
  thumbnailUrl: string | null;
  type: 'asset' | 'board' | 'contextSourceFieldValue';
  versionId: string | null;
};

/** Only strings are written, so a null attr leaves no `data-…="null"` behind. */
const buildPillDataAttribute = (name: WorkspaceAgentPillAttrName, defaultValue: string | null) => ({
  default: defaultValue,
  // Paste parsing is node-level only (`buildSanitizedPastedWorkspaceAgentPillAttrs`) so clipboard
  // display attrs cannot override sanitized reference ids (see workspaceAgentSkillMoniker).
  renderHTML: (attributes: Record<string, unknown>) => {
    const value = attributes[name];

    return typeof value === 'string' ? { [PILL_DATA_ATTRIBUTES[name]]: value } : {};
  },
});

/** What the reader guarantees on top of the node's attrs: the reference the `type` needs is there. */
export type WorkspaceAgentPillNodeReference =
  | (WorkspaceAgentPillNodeAttrs & { assetId: string; type: 'asset' })
  | (WorkspaceAgentPillNodeAttrs & { boardId: string; type: 'board' })
  | (WorkspaceAgentPillNodeAttrs & {
      contextSourceFieldValueId: string;
      contextSourceId: string;
      type: 'contextSourceFieldValue';
    });

/**
 * Fails whole rather than defaulting `type`: a corrupt node must not become a confident wrong
 * reference. A node without the reference its `type` needs fails too — it would render a chip the
 * serializer then refuses, arming Send on an effectively empty composer. `versionId` stays optional.
 */
export const readWorkspaceAgentPillNodeAttrs = (
  attrs: Record<string, unknown>,
): WorkspaceAgentPillNodeReference | null => {
  const { id, type } = attrs;

  if (typeof id !== 'string' || id.length === 0) {
    return null;
  }

  if (type !== 'asset' && type !== 'board' && type !== 'contextSourceFieldValue') {
    return null;
  }

  const assetId = typeof attrs.assetId === 'string' && attrs.assetId.length > 0 ? attrs.assetId : null;
  const boardId = typeof attrs.boardId === 'string' && attrs.boardId.length > 0 ? attrs.boardId : null;
  const contextSourceId =
    typeof attrs.contextSourceId === 'string' && attrs.contextSourceId.length > 0 ? attrs.contextSourceId : null;
  const contextSourceFieldValueId =
    typeof attrs.contextSourceFieldValueId === 'string' && attrs.contextSourceFieldValueId.length > 0
      ? attrs.contextSourceFieldValueId
      : null;

  const durableWireEntityType: WorkspaceAgentPillDurableWireEntityType | null =
    attrs.durableWireEntityType === 'asset' || attrs.durableWireEntityType === 'assetVersion'
      ? attrs.durableWireEntityType
      : null;

  const common = {
    assetType: isWorkspaceAgentAssetClipType(attrs.assetType) ? attrs.assetType : null,
    durableWireEntityType,
    ext: typeof attrs.ext === 'string' && attrs.ext.length > 0 ? attrs.ext : null,
    id,
    label: typeof attrs.label === 'string' ? attrs.label : '',
    thumbnailUrl: typeof attrs.thumbnailUrl === 'string' ? attrs.thumbnailUrl : null,
    versionId: typeof attrs.versionId === 'string' ? attrs.versionId : null,
  };

  if (type === 'board') {
    return boardId ? { ...common, assetId, boardId, contextSourceFieldValueId, contextSourceId, type } : null;
  }

  // Both halves or neither: a value id with no kit is not a reference the backend can resolve.
  if (type === 'contextSourceFieldValue') {
    return contextSourceId && contextSourceFieldValueId
      ? { ...common, assetId, boardId, contextSourceFieldValueId, contextSourceId, type }
      : null;
  }

  return assetId ? { ...common, assetId, boardId, contextSourceFieldValueId, contextSourceId, type } : null;
};

/** Pasted pill HTML: accept only UUID reference ids (mirrors skill moniker paste gate). */
const isPastedWorkspaceAgentPillElementTrusted = (element: HTMLElement): boolean => {
  const type = element.getAttribute(PILL_DATA_ATTRIBUTES.type);
  const id = element.getAttribute(PILL_DATA_ATTRIBUTES.id);

  if (
    !id ||
    !isDurableMonikerWireUuid(id) ||
    (type !== 'asset' && type !== 'board' && type !== 'contextSourceFieldValue')
  ) {
    return false;
  }

  if (type === 'contextSourceFieldValue') {
    const contextSourceId = element.getAttribute(PILL_DATA_ATTRIBUTES.contextSourceId);
    const contextSourceFieldValueId = element.getAttribute(PILL_DATA_ATTRIBUTES.contextSourceFieldValueId);

    return (
      !!contextSourceId &&
      !!contextSourceFieldValueId &&
      isDurableMonikerWireUuid(contextSourceId) &&
      isDurableMonikerWireUuid(contextSourceFieldValueId)
    );
  }

  const referenceId = element.getAttribute(
    type === 'board' ? PILL_DATA_ATTRIBUTES.boardId : PILL_DATA_ATTRIBUTES.assetId,
  );

  return !!referenceId && isDurableMonikerWireUuid(referenceId);
};

const PASTED_PILL_PLACEHOLDER_LABEL: Record<'asset' | 'board' | 'contextSourceFieldValue', string> = {
  asset: 'Asset',
  board: 'Board',
  contextSourceFieldValue: 'Brand kit image',
};

/**
 * Pasted pill HTML may carry spoofed labels/thumbnails. Keep only trusted reference ids and generic
 * placeholder chrome until workspace fetches resolve the chip (same labels as durable hydrate).
 */
export const buildSanitizedPastedWorkspaceAgentPillAttrs = (
  element: HTMLElement,
): Record<string, string | null> | false => {
  if (!isPastedWorkspaceAgentPillElementTrusted(element)) {
    return false;
  }

  const type = element.getAttribute(PILL_DATA_ATTRIBUTES.type);
  const id = element.getAttribute(PILL_DATA_ATTRIBUTES.id);

  if (!id || (type !== 'asset' && type !== 'board' && type !== 'contextSourceFieldValue')) {
    return false;
  }

  const placeholder = {
    assetId: null,
    assetType: null,
    boardId: null,
    contextSourceFieldValueId: null,
    contextSourceId: null,
    durableWireEntityType: null,
    ext: null,
    id,
    label: PASTED_PILL_PLACEHOLDER_LABEL[type],
    thumbnailUrl: null,
    type,
    versionId: null,
  };

  if (type === 'board') {
    return { ...placeholder, boardId: element.getAttribute(PILL_DATA_ATTRIBUTES.boardId) };
  }

  if (type === 'contextSourceFieldValue') {
    return {
      ...placeholder,
      contextSourceFieldValueId: element.getAttribute(PILL_DATA_ATTRIBUTES.contextSourceFieldValueId),
      contextSourceId: element.getAttribute(PILL_DATA_ATTRIBUTES.contextSourceId),
    };
  }

  return { ...placeholder, assetId: element.getAttribute(PILL_DATA_ATTRIBUTES.assetId) };
};

/** Structural minimum of a ProseMirror node, so a walk can run on plain objects too. */
type WorkspaceAgentPillDocumentNode = {
  attrs: Record<string, unknown>;
  isText?: boolean;
  text?: string;
  type: { name: string };
};

/** A real `Node` satisfies this, and so does a stub. */
export type WorkspaceAgentPillDocument = {
  descendants: (
    /** `false` stops the walk from descending, as ProseMirror's own does. */
    callback: (node: WorkspaceAgentPillDocumentNode) => boolean | undefined,
  ) => void;
};

export const WorkspaceAgentPillExtension = Node.create({
  addAttributes() {
    return {
      assetId: buildPillDataAttribute('assetId', null),
      assetType: buildPillDataAttribute('assetType', null),
      boardId: buildPillDataAttribute('boardId', null),
      contextSourceFieldValueId: buildPillDataAttribute('contextSourceFieldValueId', null),
      contextSourceId: buildPillDataAttribute('contextSourceId', null),
      durableWireEntityType: buildPillDataAttribute('durableWireEntityType', null),
      ext: buildPillDataAttribute('ext', null),
      id: buildPillDataAttribute('id', ''),
      label: buildPillDataAttribute('label', ''),
      thumbnailUrl: buildPillDataAttribute('thumbnailUrl', null),
      type: buildPillDataAttribute('type', null),
      versionId: buildPillDataAttribute('versionId', null),
    };
  },

  addNodeView() {
    return ReactNodeViewRenderer(PrivateWorkspaceAgentPillNodeView);
  },

  atom: true,
  draggable: false,
  group: 'inline',
  inline: true,
  name: WORKSPACE_AGENT_PILL_NODE_NAME,

  /** `false` rejects the rule so an unusable marker span stays plain text; `null` accepts. */
  parseHTML() {
    return [
      {
        getAttrs: (element: HTMLElement) => buildSanitizedPastedWorkspaceAgentPillAttrs(element),
        tag: `span[${PILL_MARKER_ATTRIBUTE}]`,
      },
    ];
  },

  /**
   * The literal `@label` text child keeps the mention readable when the chip is pasted as HTML into
   * an editor that does not know this node — `renderText` only feeds the plain-text clipboard. It is
   * a static child, not a content hole (`0`): a leaf spec with a hole makes `DOMSerializer` throw on
   * copy and `getHTML()`, and `parseHTML` reads the data attrs only, so the text never round-trips.
   */
  renderHTML({ HTMLAttributes, node }) {
    const label = typeof node.attrs.label === 'string' ? node.attrs.label : '';

    return ['span', mergeAttributes({ [PILL_MARKER_ATTRIBUTE]: '' }, HTMLAttributes), label ? `@${label}` : ''];
  },

  /** Without this an atom contributes nothing to `getText()`. Reading form only, not the wire moniker. */
  renderText({ node }) {
    const attrs = readWorkspaceAgentPillNodeAttrs(node.attrs);

    return attrs ? `@${attrs.label}` : '';
  },

  selectable: true,
});
